import { useState, useCallback, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useWallet } from "@/contexts/WalletContext";
import { useWallet as useSolanaWallet, useConnection } from "@solana/wallet-adapter-react";
import { Connection, PublicKey, VersionedTransaction } from "@solana/web3.js";
import { toast } from "@/hooks/use-toast";
import { buildBondingCurveTradeTransaction } from "@/lib/solana-transactions";
import {
  BondingCurveState,
  calculateBuyOutput,
  calculateSellOutput,
  calculatePrice,
  INITIAL_TOKEN_SUPPLY,
} from "@/lib/bonding-curve";
import { logger } from "@/lib/logger";
import { validateCompleteTokenDeployment } from "@/lib/anchor/validation";
import { ExecuteTradeResult } from "@/types/trading";
import { DEXAggregator, getSolMint } from "@/lib/dex-integration";
import { useSecurityValidation, SecurityValidator } from "@/lib/security";

export interface Trade {
  id: string;
  coin_id: string;
  wallet: string | null;
  type: "buy" | "sell";
  amount_sol: number | null;
  amount_token: number | null;
  timestamp: number | null;
  tx_hash: string | null;
  status: string | null;
  priority_fee: number | null;
  priority_position: number | null;
}

export interface TradeInput {
  coinId: string;
  tradeType: "buy" | "sell";
  amount: number;
  slippageTolerance?: number;
  priorityFee?: number;
}

export function useTrading(coinId: string) {
  const { connected, publicKey, balance } = useWallet();
  const { connection } = useConnection();
  const wallet = useSolanaWallet();
  const queryClient = useQueryClient();
  const [isProcessing, setIsProcessing] = useState(false);

  // Fetch user's token balance
  const tokenBalanceQuery = useQuery({
    queryKey: ["token-balance", coinId, publicKey],
    queryFn: async () => {
      if (!publicKey) return 0;

      const { data } = await supabase
        .from("token_holdings")
        .select("amount")
        .eq("coin_id", coinId)
        .eq("user_id", publicKey)
        .maybeSingle();

      return data?.amount ?? 0;
    },
    enabled: !!publicKey && !!coinId,
  });

  const tokenBalance = tokenBalanceQuery.data ?? 0;
  const isTokenBalanceLoading = tokenBalanceQuery.isLoading;

  // Fetch recent trades
  const { data: recentTrades = [] } = useQuery({
    queryKey: ["trades", coinId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("trades")
        .select("*")
        .eq("coin_id", coinId)
        .order("timestamp", { ascending: false })
        .limit(50);

      if (error) throw error;
      return data as Trade[];
    },
    enabled: !!coinId,
  });

  // Fetch coin data for bonding curve state
  const { data: coin } = useQuery({
    queryKey: ["coin", coinId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("coins")
        .select("*")
        .eq("id", coinId)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!coinId,
  });

  // Reconstruct bonding curve state
  const bondingCurveState: BondingCurveState = useMemo(() => {
    if (!coin) {
      return {
        solReserve: 1,
        tokenReserve: INITIAL_TOKEN_SUPPLY,
        totalSupply: INITIAL_TOKEN_SUPPLY,
        k: INITIAL_TOKEN_SUPPLY,
      };
    }

    const price = coin.price ?? 0.00001;
    const marketCap = coin.market_cap ?? 5000;
    const solReserve = Math.max(1, marketCap / 200);
    const tokenReserve = solReserve / price;

    return {
      solReserve,
      tokenReserve,
      totalSupply: INITIAL_TOKEN_SUPPLY,
      k: solReserve * tokenReserve,
    };
  }, [coin]);

  // Trade mutation
  const tradeMutation = useMutation({
    mutationFn: async (input: TradeInput): Promise<ExecuteTradeResult> => {
      if (!connected || !publicKey || !wallet.signTransaction) {
        throw new Error("Wallet not connected");
      }

      if (!connection) {
        throw new Error("Connection not available");
      }

      try {
        if (!coin?.mint_address) {
          throw new Error("Token mint address not found");
        }

        const mintAddress = coin.mint_address;

        // Initialize DEX Aggregator for real trading
        const dexAggregator = new DEXAggregator(connection);
        const solMint = getSolMint();

        // Prepare trade parameters
        const tradeParams = {
          inputMint: input.tradeType === "buy" ? solMint : new PublicKey(mintAddress),
          outputMint: input.tradeType === "buy" ? new PublicKey(mintAddress) : solMint,
          inputAmount: input.tradeType === "buy"
            ? BigInt(Math.floor(input.amount * 1e9)) // SOL to lamports
            : BigInt(Math.floor(input.amount * 1_000_000_000)), // Token amount (adjusting for 9 decimals)
          slippageBps: Math.floor((input.slippageTolerance || 0.5) * 100),
          userWallet: new PublicKey(publicKey.toString()),
        };

        // Get swap transaction from DEX aggregator (returns best quote + transaction)
        const swapResult = await dexAggregator.executeSwap(tradeParams);

        if (!swapResult.success || !swapResult.transaction) {
          throw new Error(swapResult.error || "Failed to build swap transaction");
        }

        // Sign and send transaction
        const signedTx = await wallet.signTransaction(swapResult.transaction);
        const signature = await connection.sendRawTransaction(signedTx.serialize(), {
          skipPreflight: false,
          preflightCommitment: 'confirmed',
        });

        logger.info('Transaction sent', { signature });

        // Wait for confirmation
        const confirmation = await connection.confirmTransaction(signature, 'confirmed');
        if (confirmation.value.err) {
          throw new Error(`Transaction failed: ${JSON.stringify(confirmation.value.err)}`);
        }

        // Call edge function to verify on-chain and update database
        const { data: result, error: functionError } = await supabase.functions.invoke('execute-trade', {
          body: {
            coinId: input.coinId,
            tradeType: input.tradeType,
            amount: input.amount,
            userWallet: publicKey.toString(),
            txSignature: signature,
          },
        });

        if (functionError) {
          throw new Error(functionError.message || "Database side verification failed");
        }

        if (!result?.success) {
          throw new Error(result?.error || "On-chain verification failed");
        }

        return result as ExecuteTradeResult;
      } catch (error) {
        console.error("Trade execution failed:", error);
        throw new Error(error instanceof Error ? error.message : "Trade execution failed");
      }
    },
    onSuccess: (result, variables) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ["coin", variables.coinId] });
      queryClient.invalidateQueries({ queryKey: ["trades", variables.coinId] });
      queryClient.invalidateQueries({ queryKey: ["token-balance", variables.coinId, publicKey] });
      queryClient.invalidateQueries({ queryKey: ["leaderboard"] });

      toast({
        title: `${variables.tradeType === "buy" ? "Buy" : "Sell"} Order Executed`,
        description: variables.tradeType === "buy"
          ? `Bought ${result.tokensOut.toLocaleString()} tokens`
          : `Sold for ${result.solOut.toFixed(6)} SOL`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Trade Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Calculate trade preview
  const calculateTradePreview = useCallback(
    (tradeType: "buy" | "sell", amount: number) => {
      if (amount <= 0) {
        return { output: 0, fee: 0, priceImpact: 0, newPrice: 0 };
      }

      if (tradeType === "buy") {
        const result = calculateBuyOutput(bondingCurveState, amount);
        return {
          output: result.tokensOut,
          fee: result.fee,
          priceImpact: result.priceImpact,
          newPrice: calculatePrice(result.newState),
        };
      } else {
        const tokenAmount = amount;
        const result = calculateSellOutput(bondingCurveState, tokenAmount);
        return {
          output: result.solOut,
          fee: result.fee,
          priceImpact: result.priceImpact,
          newPrice: calculatePrice(result.newState),
        };
      }
    },
    [bondingCurveState]
  );

  // Execute trade with security validation
  const executeTrade = useCallback(
    async (tradeType: "buy" | "sell", amount: number, slippageTolerance = 0.5) => {
      // Security validation
      if (!connected) {
        throw new Error("Wallet not connected");
      }

      // Validate amount
      const amountValidation = SecurityValidator.validateAmount(amount, balance);
      if (!amountValidation.valid) {
        throw new Error(amountValidation.error || "Invalid amount");
      }

      if (tradeType === "buy" && amount > balance) {
        throw new Error("Insufficient SOL balance");
      }

      if (tradeType === "sell") {
        const tokenAmount = amount;
        if (tokenAmount > tokenBalance) {
          throw new Error("Insufficient token balance");
        }
      }

      // Validate slippage
      const slippageValidation = SecurityValidator.validateAmount(slippageTolerance);
      if (!slippageValidation.valid) {
        throw new Error("Invalid slippage tolerance");
      }

      // Rate limiting check
      const rateLimit = SecurityValidator.checkRateLimit(publicKey?.toString() || 'anonymous', 10, 60000);
      if (!rateLimit.allowed) {
        throw new Error(`Rate limit exceeded. Try again in ${Math.ceil(rateLimit.resetTime / 1000)} seconds`);
      }

      setIsProcessing(true);
      try {
        return await logger.withTimer(
          `execute_trade_${tradeType}`,
          async () => {
            return await tradeMutation.mutateAsync({
              coinId,
              tradeType,
              amount,
              slippageTolerance,
            });
          },
          {
            coinId,
            tradeType,
            amount,
            slippageTolerance,
            userWallet: publicKey?.toString(),
          }
        );
      } finally {
        setIsProcessing(false);
      }
    },
    [connected, balance, tokenBalance, coinId, tradeMutation, publicKey]
  );

  return {
    // State
    connected,
    publicKey,
    balance,
    tokenBalance,
    coin,
    recentTrades,
    bondingCurveState,
    isProcessing: isProcessing || tradeMutation.isPending,
    isTokenBalanceLoading,

    // Actions
    executeTrade,
    calculateTradePreview,

    // Computed
    currentPrice: calculatePrice(bondingCurveState),
  };
}

// Hook for trade history
export function useTradeHistory(walletAddress?: string, coinId?: string) {
  return useQuery({
    queryKey: ["trade-history", walletAddress, coinId],
    queryFn: async () => {
      let query = supabase
        .from("trades")
        .select("*, coins(name, ticker, image)")
        .order("timestamp", { ascending: false })
        .limit(100);

      if (walletAddress) {
        query = query.eq("wallet", walletAddress);
      }

      if (coinId) {
        query = query.eq("coin_id", coinId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data;
    },
    enabled: !!(walletAddress || coinId),
  });
}

// Hook for market depth / order book simulation
export function useMarketDepth(coinId: string) {
  const { data: coin } = useQuery({
    queryKey: ["coin", coinId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("coins")
        .select("*")
        .eq("id", coinId)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!coinId,
  });

  // Generate synthetic order book from bonding curve
  const orderBook = useMemo(() => {
    if (!coin) return { bids: [], asks: [] };

    const currentPrice = coin.price ?? 0.00001;
    const bids = [];
    const asks = [];

    // Generate bid levels (buy orders at lower prices)
    for (let i = 1; i <= 10; i++) {
      const priceLevel = currentPrice * (1 - i * 0.01);
      // Deterministic volume based on price level (more volume closer to price)
      const volume = (10000 / (i + 1)) + (priceLevel * 1000);
      bids.push({ price: priceLevel, volume, total: priceLevel * volume });
    }

    // Generate ask levels (sell orders at higher prices)
    for (let i = 1; i <= 10; i++) {
      const priceLevel = currentPrice * (1 + i * 0.01);
      // Deterministic volume
      const volume = (10000 / (i + 1)) + (priceLevel * 1000);
      asks.push({ price: priceLevel, volume, total: priceLevel * volume });
    }

    return { bids, asks };
  }, [coin]);

  return { orderBook, currentPrice: coin?.price ?? 0 };
}
